import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { reportsAPI, customersAPI, brandsAPI, categoriesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import {
    BarChart3, Wallet, Users, Package, TrendingUp, AlertCircle, Receipt, ChevronLeft, ChevronRight, ArrowLeft,
} from 'lucide-react';
import { DateRangeBar, ExportBar, KPICard, ReportLoader, ReportEmpty, ReportError, useCurrency, downloadCsv } from '../../components/reports/ReportKit';
import PaymentBreakdownPanel from '../../components/reports/PaymentBreakdownPanel';
import { printReportDocument } from '../../lib/printReport';

const initialFilters = {
    start: null, end: null,
    customer_id: 'all', brand_id: 'all', category_id: 'all',
    payment_status: 'all', sale_type: 'all',
};

const SalesReport = () => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    const fmtMoney = useCurrency();
    const [filters, setFilters] = useState(initialFilters);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const pageSize = 50;

    const [customers, setCustomers] = useState([]);
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [c, b, cat] = await Promise.all([
                    customersAPI.getAll().catch(() => ({ data: [] })),
                    brandsAPI.getAll().catch(() => ({ data: [] })),
                    categoriesAPI.getAll().catch(() => ({ data: [] })),
                ]);
                setCustomers(c.data || []);
                setBrands(b.data || []);
                setCategories(cat.data || []);
        } catch (err) {
            // Non-fatal — filter dropdowns just render without labels.
            console.error('[SalesReport] failed to load filter data:', err?.response?.status || err?.message);
        }
        })();
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        setFilters((f) => ({ ...f, start, end }));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: pageSize };
            if (filters.start) params.start_date = format(filters.start, 'yyyy-MM-dd');
            if (filters.end) params.end_date = format(filters.end, 'yyyy-MM-dd');
            if (filters.customer_id !== 'all') params.customer_id = filters.customer_id;
            if (filters.brand_id !== 'all') params.brand_id = filters.brand_id;
            if (filters.category_id !== 'all') params.category_id = filters.category_id;
            if (filters.payment_status !== 'all') params.payment_status = filters.payment_status;
            if (filters.sale_type !== 'all') params.sale_type = filters.sale_type;
            const { data: res } = await reportsAPI.salesAnalytics(params);
            setData(res);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [filters, page, t]);

    useEffect(() => {
        if (filters.start && filters.end) load();
    }, [load, filters.start, filters.end]);

    const setPreset = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setFilters((f) => ({ ...f, start, end }));
        setPage(1);
    };

    const exportCsv = () => {
        if (!data?.rows?.length) {
            toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
            return;
        }
        const columns = [
            { key: 'invoice_number', label: t('entities:reports.columns.invoice') },
            { key: 'created_at', label: t('entities:reports.columns.date'), value: (r) => r.created_at?.slice(0, 10) },
            { key: 'company_name', label: t('entities:reports.columns.company') },
            { key: 'subtotal', label: 'HT' },
            { key: 'discount_amount', label: t('entities:reports.sales.discount', { defaultValue: 'Discount' }) },
            { key: 'vat_amount', label: 'TVA' },
            { key: 'amount', label: 'TTC' },
            { key: 'paid_amount', label: t('entities:reports.sales.paid', { defaultValue: 'Paid' }) },
            { key: 'outstanding', label: t('entities:reports.sales.outstanding', { defaultValue: 'Outstanding' }) },
            { key: 'created_by_name', label: t('entities:reports.columns.recorded_by') },
        ];
        downloadCsv(`sales-report-${format(filters.start, 'yyyy-MM-dd')}_${format(filters.end, 'yyyy-MM-dd')}.csv`, columns, data.rows);
        toast.success(t('entities:reports.toasts.exported'));
    };

    const handlePrint = () => {
        if (!data?.rows?.length) {
            toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
            return;
        }
        const lang = i18n.language || 'fr';
        const moneyTxt = (v) => fmtMoney(v).replace(/\u00a0/g, ' ');
        const period = (filters.start && filters.end)
            ? `${format(filters.start, 'dd MMM yyyy')} — ${format(filters.end, 'dd MMM yyyy')}`
            : '';
        const rows = data.rows.map((r) => `
            <tr>
                <td style="font-family:'Courier New',monospace;">${r.invoice_number || '—'}</td>
                <td>${(r.created_at || '').slice(0, 10)}</td>
                <td>${r.company_name || '—'}</td>
                <td class="num">${moneyTxt(r.subtotal || 0)}</td>
                <td class="num">${moneyTxt(r.vat_amount || 0)}</td>
                <td class="num" style="font-weight:600;">${moneyTxt(r.amount || 0)}</td>
                <td class="num" style="color:#2A9D8F;">${moneyTxt(r.paid_amount || 0)}</td>
                <td class="num" style="color:${r.outstanding > 0 ? '#E76F51' : '#888'};">${moneyTxt(r.outstanding || 0)}</td>
            </tr>
        `).join('');
        const metaHtml = `
            <div class="kpi-strip">
                <div class="kpi accent"><span class="k">${lang.startsWith('fr') ? 'CA TTC' : 'Revenue TTC'}</span><span class="v">${moneyTxt(k.revenue_ttc)}</span></div>
                <div class="kpi"><span class="k">HT</span><span class="v">${moneyTxt(k.revenue_ht)}</span></div>
                <div class="kpi"><span class="k">TVA</span><span class="v">${moneyTxt(k.vat_collected)}</span></div>
                <div class="kpi success"><span class="k">${lang.startsWith('fr') ? 'Encaissé' : 'Collected'}</span><span class="v">${moneyTxt(k.paid_total)}</span></div>
                <div class="kpi danger"><span class="k">${lang.startsWith('fr') ? 'Impayé' : 'Outstanding'}</span><span class="v">${moneyTxt(k.outstanding_total)}</span></div>
            </div>
        `;

        // Build the Payment Breakdown block that mirrors the on-screen panel.
        // The print window uses a hand-built HTML string (not the React tree)
        // so we render an equivalent <table> here — otherwise the panel would
        // be missing from PDF / physical prints.
        const pb = data.payment_breakdown || null;
        const pbLabels = lang.startsWith('fr')
            ? { title: 'Répartition des paiements', method: 'Mode', amount: 'Montant',
                cash: 'Espèces', card: 'Carte', bank: 'Virement bancaire',
                cheque: 'Chèque', others: 'Autres',
                totalPaid: 'Total payé', outstanding: 'Solde dû',
                grand: 'Total général (facturé)' }
            : { title: 'Payment Breakdown', method: 'Method', amount: 'Amount',
                cash: 'Cash', card: 'Card', bank: 'Bank Transfer',
                cheque: 'Cheque', others: 'Others',
                totalPaid: 'Total Paid', outstanding: 'Outstanding Due',
                grand: 'Grand Total (invoiced)' };
        const pbDenom = pb ? Math.max(0.0001, (pb.total_paid || 0) + (pb.total_due || 0)) : 1;
        const pbPct = (v) => `${(((v || 0) / pbDenom) * 100).toFixed(1)}%`;
        const pbPctNum = (v) => Math.min(100, ((v || 0) / pbDenom) * 100);
        // Inline horizontal bar — sized proportional to the row's share of
        // grand total. Colour comes from the semantic palette used across
        // the app (cash=teal, card=slate, bank=amber, cheque=violet, etc.).
        const pbBar = (val, color) => `
            <div class="bar-track">
                <div class="bar-fill" style="width:${pbPctNum(val).toFixed(1)}%;background:${color};"></div>
            </div>
        `;
        const pbRow = (label, val, color) => (
            (val || 0) > 0
                ? `<tr>
                       <td>${label}</td>
                       <td class="num">${moneyTxt(val)}</td>
                       <td class="bar">${pbBar(val, color)}</td>
                       <td class="num" style="color:#666;font-size:11px;">${pbPct(val)}</td>
                   </tr>`
                : ''
        );
        const paymentBreakdownHtml = pb ? `
            <div class="payment-breakdown">
                <h3>${pbLabels.title}</h3>
                <table class="pb-table">
                    <thead><tr>
                        <th>${pbLabels.method}</th>
                        <th class="num">${pbLabels.amount}</th>
                        <th></th>
                        <th class="num" style="width:80px;">%</th>
                    </tr></thead>
                    <tbody>
                        ${pbRow(pbLabels.cash,   pb.total_cash,          '#2A9D8F')}
                        ${pbRow(pbLabels.card,   pb.total_card,          '#264653')}
                        ${pbRow(pbLabels.bank,   pb.total_bank_transfer, '#E9C46A')}
                        ${pbRow(pbLabels.cheque, pb.total_cheque,        '#7C6BAD')}
                        ${pbRow(pbLabels.others, pb.total_others,        '#9CA3AF')}
                    </tbody>
                    <tfoot>
                        <tr class="pb-paid">
                            <td>${pbLabels.totalPaid}</td>
                            <td class="num">${moneyTxt(pb.total_paid)}</td>
                            <td class="bar">${pbBar(pb.total_paid, '#2A9D8F')}</td>
                            <td class="num pct">${pbPct(pb.total_paid)}</td>
                        </tr>
                        ${(pb.total_due || 0) > 0 ? `
                        <tr class="pb-due">
                            <td>${pbLabels.outstanding}</td>
                            <td class="num">${moneyTxt(pb.total_due)}</td>
                            <td class="bar">${pbBar(pb.total_due, '#E76F51')}</td>
                            <td class="num pct">${pbPct(pb.total_due)}</td>
                        </tr>` : ''}
                        <tr class="pb-grand">
                            <td>${pbLabels.grand}</td>
                            <td class="num">${moneyTxt(pb.grand_total)}</td>
                            <td class="bar">${pbBar(pb.grand_total, '#1A1A1A')}</td>
                            <td class="num pct">100.0%</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        ` : '';

        const contentHtml = `
            ${paymentBreakdownHtml}
            <table class="report-table">
                <thead><tr>
                    <th>${lang.startsWith('fr') ? 'Facture N°' : 'Invoice N°'}</th>
                    <th>${lang.startsWith('fr') ? 'Date' : 'Date'}</th>
                    <th>${lang.startsWith('fr') ? 'Client' : 'Customer'}</th>
                    <th class="num">HT</th>
                    <th class="num">TVA</th>
                    <th class="num">TTC</th>
                    <th class="num">${lang.startsWith('fr') ? 'Réglé' : 'Paid'}</th>
                    <th class="num">${lang.startsWith('fr') ? 'Impayé' : 'Outstanding'}</th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>
        `;
        printReportDocument({
            title: lang.startsWith('fr') ? 'Rapport des Ventes' : 'Sales Report',
            subtitle: period,
            metaHtml,
            contentHtml,
            language: lang,
        });
    };

    const k = data?.kpis;
    const total = data?.pagination?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div data-testid="sales-report-page" className="space-y-6 animate-fadeIn">
            <div className="flex items-center justify-between no-print">
                <div>
                    <Link to="/reports" className="text-xs text-gray-500 hover:text-[#C08478] flex items-center gap-1 mb-2">
                        <ArrowLeft size={12} /> {t('entities:reports.hub.title')}
                    </Link>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:reports.hub.cards.sales.title')}</h1>
                    <p className="text-gray-500 text-sm mt-1">{t('entities:reports.hub.cards.sales.description')}</p>
                </div>
                <ExportBar onCsv={exportCsv} onPrint={handlePrint} disabled={!data?.rows?.length} />
            </div>

            <DateRangeBar
                start={filters.start}
                end={filters.end}
                onChange={({ start, end }) => { setFilters((f) => ({ ...f, start, end })); setPage(1); }}
                onPreset={setPreset}
            >
                <Select value={filters.customer_id} onValueChange={(v) => { setFilters((f) => ({ ...f, customer_id: v })); setPage(1); }}>
                    <SelectTrigger className="h-9 w-44" data-testid="filter-customer">
                        <SelectValue placeholder={t('entities:customers.title')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('common:common.all')} — {t('entities:customers.title')}</SelectItem>
                        {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filters.brand_id} onValueChange={(v) => { setFilters((f) => ({ ...f, brand_id: v })); setPage(1); }}>
                    <SelectTrigger className="h-9 w-36" data-testid="filter-brand">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:products.filters.all_brands')}</SelectItem>
                        {brands.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filters.category_id} onValueChange={(v) => { setFilters((f) => ({ ...f, category_id: v })); setPage(1); }}>
                    <SelectTrigger className="h-9 w-40" data-testid="filter-category">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:products.filters.all_categories')}</SelectItem>
                        {categories.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filters.payment_status} onValueChange={(v) => { setFilters((f) => ({ ...f, payment_status: v })); setPage(1); }}>
                    <SelectTrigger className="h-9 w-36" data-testid="filter-payment-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:reports.sales.filters.all_payments', { defaultValue: 'All statuses' })}</SelectItem>
                        <SelectItem value="paid">{t('entities:reports.sales.filters.paid', { defaultValue: 'Paid' })}</SelectItem>
                        <SelectItem value="unpaid">{t('entities:reports.sales.filters.unpaid', { defaultValue: 'Unpaid' })}</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filters.sale_type} onValueChange={(v) => { setFilters((f) => ({ ...f, sale_type: v })); setPage(1); }}>
                    <SelectTrigger className="h-9 w-36" data-testid="filter-sale-type">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:reports.sales.filters.all_types', { defaultValue: 'All types' })}</SelectItem>
                        <SelectItem value="invoice">{t('entities:reports.sales.filters.invoice', { defaultValue: 'Invoices' })}</SelectItem>
                        <SelectItem value="sale">{t('entities:reports.sales.filters.sale', { defaultValue: 'Quick sales' })}</SelectItem>
                    </SelectContent>
                </Select>
            </DateRangeBar>

            {loading && <ReportLoader />}
            {error && !loading && <ReportError message={error} onRetry={load} />}

            {!loading && !error && k && (
                <>
                    {/* KPI grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <KPICard testId="kpi-revenue-ttc" icon={Wallet} accent="default" label={t('entities:reports.sales.kpi.revenue_ttc')} value={fmtMoney(k.revenue_ttc)} hint={`HT: ${fmtMoney(k.revenue_ht)}`} />
                        <KPICard testId="kpi-vat" icon={Receipt} accent="warning" label={t('entities:reports.sales.kpi.vat_collected')} value={fmtMoney(k.vat_collected)} />
                        <KPICard testId="kpi-gross-profit" icon={TrendingUp} accent="success" label={t('entities:reports.sales.kpi.gross_profit')} value={fmtMoney(k.gross_profit)} hint={`${k.gross_margin_pct}%`} />
                        <KPICard testId="kpi-aov" icon={BarChart3} accent="info" label={t('entities:reports.sales.kpi.aov')} value={fmtMoney(k.aov)} hint={`${k.total_count} ${t('entities:reports.sales.kpi.transactions', { defaultValue: 'tx' })}`} />
                        <KPICard testId="kpi-outstanding" icon={AlertCircle} accent="danger" label={t('entities:reports.sales.kpi.outstanding')} value={fmtMoney(k.outstanding_total)} hint={`${k.unpaid_count} ${t('entities:reports.sales.kpi.unpaid', { defaultValue: 'unpaid' })}`} />
                        <KPICard testId="kpi-paid" icon={Package} accent="accent" label={t('entities:reports.sales.kpi.paid_total')} value={fmtMoney(k.paid_total)} hint={`${k.paid_count} ${t('entities:reports.sales.kpi.paid', { defaultValue: 'paid' })}`} />
                    </div>

                    {/* Payment Breakdown — cash / card / bank_transfer / cheque
                        / others aggregated over the report period, plus
                        settled dues and outstanding balances. Backend
                        already handles split payments via _payment_amounts. */}
                    {data.payment_breakdown && (
                        <PaymentBreakdownPanel summary={data.payment_breakdown} />
                    )}

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 print-card-grid">
                        <Card className="border-0 shadow-sm">
                            <CardHeader><CardTitle className="text-sm">{t('entities:reports.sales.charts.sales_trend')}</CardTitle></CardHeader>
                            <CardContent>
                                {data.charts.sales_trend.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                    <ResponsiveContainer width="100%" height={240}>
                                        <LineChart data={data.charts.sales_trend}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <Tooltip formatter={(v) => fmtMoney(v)} />
                                            <Line type="monotone" dataKey="revenue" stroke="#2A9D8F" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>
                        <Card className="border-0 shadow-sm">
                            <CardHeader><CardTitle className="text-sm">{t('entities:reports.sales.charts.monthly_revenue')}</CardTitle></CardHeader>
                            <CardContent>
                                {data.charts.monthly_revenue.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                    <ResponsiveContainer width="100%" height={240}>
                                        <BarChart data={data.charts.monthly_revenue}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 11 }} />
                                            <Tooltip formatter={(v) => fmtMoney(v)} />
                                            <Bar dataKey="revenue" fill="#C08478" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>
                        <Card className="border-0 shadow-sm">
                            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> {t('entities:reports.sales.charts.top_customers')}</CardTitle></CardHeader>
                            <CardContent>
                                {data.charts.top_customers.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('entities:customers.title')}</TableHead>
                                                <TableHead className="text-right">{t('common:common.total')}</TableHead>
                                                <TableHead className="text-right">{t('entities:reports.columns.invoice')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.charts.top_customers.map((c, i) => (
                                                <TableRow key={c.customer_id || i}>
                                                    <TableCell className="font-medium">{c.name}</TableCell>
                                                    <TableCell className="text-right">{fmtMoney(c.revenue_ttc)}</TableCell>
                                                    <TableCell className="text-right text-xs text-gray-500">{c.invoice_count}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                        <Card className="border-0 shadow-sm">
                            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Package size={14} /> {t('entities:reports.sales.charts.top_products')}</CardTitle></CardHeader>
                            <CardContent>
                                {data.charts.top_products.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('entities:products.columns.name')}</TableHead>
                                                <TableHead className="text-right">{t('entities:products.columns.stock')}</TableHead>
                                                <TableHead className="text-right">{t('common:common.total')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.charts.top_products.map((p, i) => (
                                                <TableRow key={p.product_id || i}>
                                                    <TableCell><span className="font-medium">{p.name}</span> <span className="text-[10px] text-gray-400 font-mono">{p.sku}</span></TableCell>
                                                    <TableCell className="text-right">{p.qty}</TableCell>
                                                    <TableCell className="text-right">{fmtMoney(p.revenue_ttc)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Detail table */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-sm">
                                {t('entities:reports.sales.detail_title', { count: total, defaultValue: `Details (${total})` })}
                            </CardTitle>
                            <div className="flex items-center gap-2 no-print">
                                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev">
                                    <ChevronLeft size={14} />
                                </Button>
                                <span className="text-xs text-gray-500">{page} / {totalPages}</span>
                                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">
                                    <ChevronRight size={14} />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {data.rows.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('entities:reports.columns.invoice')}</TableHead>
                                                <TableHead>{t('entities:reports.columns.date')}</TableHead>
                                                <TableHead>{t('entities:reports.columns.company')}</TableHead>
                                                <TableHead className="text-right">HT</TableHead>
                                                <TableHead className="text-right">TVA</TableHead>
                                                <TableHead className="text-right">TTC</TableHead>
                                                <TableHead className="text-right">{t('entities:reports.sales.paid', { defaultValue: 'Paid' })}</TableHead>
                                                <TableHead className="text-right">{t('entities:reports.sales.outstanding', { defaultValue: 'Outstanding' })}</TableHead>
                                                <TableHead>{t('entities:reports.columns.recorded_by')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.rows.map((r) => (
                                                <TableRow key={r.id} data-testid={`row-${r.invoice_number}`}>
                                                    <TableCell>
                                                        <Link to={`/transactions?focus=${r.id}`} className="font-mono text-sm font-medium text-[#1A1A1A] hover:text-[#C08478]">
                                                            {r.invoice_number}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-500">{r.created_at?.slice(0, 10)}</TableCell>
                                                    <TableCell className="text-sm">{r.company_name || '-'}</TableCell>
                                                    <TableCell className="text-right">{fmtMoney(r.subtotal)}</TableCell>
                                                    <TableCell className="text-right">{fmtMoney(r.vat_amount)}</TableCell>
                                                    <TableCell className="text-right font-semibold">{fmtMoney(r.amount)}</TableCell>
                                                    <TableCell className="text-right text-[#2A9D8F]">{fmtMoney(r.paid_amount)}</TableCell>
                                                    <TableCell className="text-right">
                                                        {r.outstanding > 0 ? (
                                                            <Badge className="bg-[#E76F51]/20 text-[#E76F51]">{fmtMoney(r.outstanding)}</Badge>
                                                        ) : <span className="text-gray-400">—</span>}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-500">{r.created_by_name}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default SalesReport;
